// Per-field test values for the Set Placeholders "Preview PDF" button.
//
// These are a Super Admin's scratch data for checking a layout — "does a long
// project name still fit in that box" — and are NOT part of the form. They are
// therefore kept OUT of the database entirely and parked in localStorage,
// which has two useful properties:
//
//   * there is no code path by which they can reach an issued document or an
//     inspection report, because the server never stores them; and
//   * they survive closing the modal, so positioning a form does not mean
//     retyping fifteen sample strings every time.
//
// Keyed per form, so two forms do not share each other's samples.

import { fieldId } from './placeholderFields';

const PREFIX = 'formSetupPreviewValues';

export const previewStorageKey = (formId?: string | number): string =>
  `${PREFIX}:${formId || 'new'}`;

/** Field id -> test value, or {} when there is nothing stored or usable. */
export const loadPreviewValues = (
  key: string,
): Record<string, string> => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Guard against anything hand-edited or left by an older shape — a bad
    // value here would otherwise throw while rendering the panel.
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    // Private-mode storage, quota, corrupt JSON — none of it is worth
    // breaking the editor over.
    return {};
  }
};

export const savePreviewValues = (key: string, fields: any[]): void => {
  try {
    const values: Record<string, string> = {};
    (fields || []).forEach(field => {
      if (field?.previewText) values[fieldId(field)] = field.previewText;
    });
    if (Object.keys(values).length) {
      window.localStorage.setItem(key, JSON.stringify(values));
    } else {
      // Cleared every value: drop the entry rather than leaving {} behind.
      window.localStorage.removeItem(key);
    }
  } catch {
    // Persistence is a convenience; failing to save must not block a preview.
  }
};

/** Re-attach stored test values to freshly loaded placeholders. */
export const applyPreviewValues = (
  fields: any[],
  values: Record<string, string>,
): any[] =>
  (fields || []).map(field => {
    const stored = values[fieldId(field)];
    return stored ? { ...field, previewText: stored } : field;
  });
